import { service } from '@ember/service';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type AnswerProfileService from 'tio-employee/services/answer-profile';
import type { RecommendationExtension as RecomendationModel } from 'tio-data/data/resources/recommendation.ext';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import ConsolidationTodo from './consolidation-todo';
import ProvideRepaymentPlanTodo from './provide-repayment-plan-todo';
import RecertifyTodo from './recertify-todo';
import RepaymentPlanTodo from './repayment-plan-todo';
import { Section } from 'tio-ui/components/layout';
import type { ObservableSource } from 'tio-employee/services/observability';

export interface DashRecommendationsSignature {
  Args: {
    loanSummary: LoanSummary;
    person: PersonModel;
    recommendation: RecomendationModel;
    source?: ObservableSource;
  };
  Element: HTMLDivElement;
}

export default class DashRecommendationsComponent extends Component<DashRecommendationsSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service('answer-profile') declare answerProfileService: AnswerProfileService;

  answerProfileState = trackedFunction(this, async () => {
    const profile = await this.answerProfileService.findOrCreateAnswerProfileForCurrentUser();
    return profile;
  });

  get answerProfile() {
    return this.answerProfileState.value;
  }

  get loanSummary() {
    return this.args.loanSummary;
  }

  get loanSummariesByType() {
    return this.loanSummary?.summaryByLoanType || [];
  }

  get loans() {
    return this.args.person.allActiveAccountLoans || [];
  }

  get shouldConsolidateLoans() {
    return !!this.loanSummariesByType.find((summary) => {
      return summary.loanType !== 'direct';
    });
  }

  /**
   * Returns true when we don't have repayment plan info on file.
   * This happens when users upload statements without plan data or loans are manually added.
   */
  get hasMissingRepaymentPlan() {
    const allRepaymentPlans = this.loanSummariesByType.flatMap((summary) => summary.repaymentPlans);
    return allRepaymentPlans.some((plan) => plan === 'noRepaymentPlan' || !plan);
  }

  /**
   * Returns true when user has a non-qualifying repayment plan (e.g., graduated, standard).
   * Does NOT return true when repayment plan data is missing - use hasMissingRepaymentPlan for that.
   */
  get shouldChangeRepaymentPlan() {
    // If repayment plan is missing, don't show "change plan" - show "provide plan" instead
    if (this.hasMissingRepaymentPlan) {
      return false;
    }
    const validRepaymentPlansForPslf = ['icr', 'ibr', 'paye', 'repaye', 'fivePercentUndergrad'];
    const allRepaymentPlans = [
      ...new Set(this.loanSummariesByType.flatMap((summary) => summary.repaymentPlans)),
    ];
    return allRepaymentPlans.every((plan) => !validRepaymentPlansForPslf.includes(plan));
  }

  get shouldLinkLoans() {
    return this.args.person.needsToLinkLoans;
  }

  get hasNoRecommendations() {
    return !(
      this.shouldConsolidateLoans ||
      this.hasMissingRepaymentPlan ||
      this.shouldChangeRepaymentPlan ||
      this.shouldLinkLoans ||
      this.needsToRecertify
    );
  }

  get hasTakenEstimator() {
    return !!this.args.recommendation;
  }

  get needsToRecertify() {
    return this.hasTakenEstimator;
  }

  <template>
    {{#unless this.hasNoRecommendations}}
      <Section>
        <:header>{{t "recommendations.default"}}</:header>
        <:body>
          {{#if this.shouldConsolidateLoans}}
            <Section>
              {{! @glint-expect-error}}
              <ConsolidationTodo @answerProfile={{this.answerProfile}} />
            </Section>
          {{/if}}
          {{#if this.hasMissingRepaymentPlan}}
            <Section>
              <ProvideRepaymentPlanTodo @source={{@source}} />
            </Section>
          {{/if}}
          {{#if this.shouldChangeRepaymentPlan}}
            <Section>
              {{! @glint-expect-error}}
              <RepaymentPlanTodo @answerProfile={{this.answerProfile}} />
            </Section>
          {{/if}}
          {{#if this.needsToRecertify}}
            {{! @glint-expect-error}}
            <RecertifyTodo @answerProfile={{this.answerProfile}} />
          {{/if}}
        </:body>
      </Section>
    {{/unless}}
  </template>
}
